/**
 * 同期の操作語彙。
 *
 * ## 出自
 * ゼロから作っていない。`Packages/BisideRemote/Sources/BisideRemote/EditIntent.swift` が
 * スマホから Mac へ編集を送る語彙として本番で動いており、その形をそのまま引き継いでいる。
 * 名前も意味も揃えてあるので、Swift 側の写像は既存の `AppRemoteBridge.applyEdit` の延長で書ける。
 *
 * ## 中核の `Op` とは別物である
 * `Packages/BisideCore/Sources/BisideCore/Ops/Op.swift` の `Op` は GRDB の `Database` を直接
 * 受け取るデータベース層の型で、通信には載せられない。**通信用（ここ）と適用用（あちら）は
 * 別物**として設計している。混ぜると必ず詰まる。
 *
 * ## 位置はクライアントが決める
 * 挿入位置（`position`）は送る側が `FractionIndex` で計算して載せる。サーバは検証するだけで
 * 計算しない。同じ計算を Swift・TypeScript・サーバの三か所に持たないための約束。
 */

/** 操作の種類。追加するときは Swift 側の写像とフィクスチャも同時に足す。 */
export type SyncOpKind =
  | "insert"
  | "update"
  | "updateNote"
  | "setCollapsed"
  | "setCompleted"
  | "move"
  | "delete"
  | "rowUpsert"
  | "rowDelete";

interface SyncOpCommon {
  /** クライアントが採番する一意な識別子。**冪等性キー**で、同じものは二度適用しない。 */
  opId: string;
  /** 対象ノード。**行の操作では対象行の識別子**（`tag` なら `key`）。 */
  nodeId: string;
}

export interface InsertOp extends SyncOpCommon {
  kind: "insert";
  parentId: string | null;
  /** 送る側が計算した位置。サーバは計算し直さない。 */
  position: string;
  text?: string;
}

export interface UpdateOp extends SyncOpCommon {
  kind: "update";
  text: string;
}

export interface UpdateNoteOp extends SyncOpCommon {
  kind: "updateNote";
  /** 空文字と空白のみは「注記なし」に正規化する（Mac 側の書き込み口と同じ扱い）。 */
  note: string | null;
}

export interface SetCollapsedOp extends SyncOpCommon {
  kind: "setCollapsed";
  value: boolean;
}

export interface SetCompletedOp extends SyncOpCommon {
  kind: "setCompleted";
  value: boolean;
}

export interface MoveOp extends SyncOpCommon {
  kind: "move";
  parentId: string | null;
  position: string;
}

/** 消さずに印を付ける。実削除はしない（同期の収束と復元のため）。 */
export interface DeleteOp extends SyncOpCommon {
  kind: "delete";
}

/**
 * 行まるごとの上書き。**Mac 版が送るのはこれ**（送信箱が積んでいる事実がそのままこの形になる）。
 * `cols` は表の列名から値への写像で、送り主が読み直した現在値をそのまま載せる。
 */
export interface RowUpsertOp extends SyncOpCommon {
  kind: "rowUpsert";
  table: string;
  cols: Record<string, string | number | null>;
}

/** 行が消えた事実。クラウド側では印を付けるだけで、実削除はしない。 */
export interface RowDeleteOp extends SyncOpCommon {
  kind: "rowDelete";
  table: string;
}

export type SyncOp =
  | InsertOp
  | UpdateOp
  | UpdateNoteOp
  | SetCollapsedOp
  | SetCompletedOp
  | MoveOp
  | DeleteOp
  | RowUpsertOp
  | RowDeleteOp;

/** サーバが連番と発行者を添えた形。クライアントはこれを受け取って確定させる。 */
export interface SyncEnvelope {
  /** ワークスペース内で単調増加。**サーバだけが振る**。 */
  seq: number;
  /** 発行したユーザー。 */
  actor: string;
  /** ISO 8601。表示用で、順序の根拠には使わない（順序は seq）。 */
  at: string;
  op: SyncOp;
}

/** 射影されたノード 1 行。列名は Mac の `node` テーブルに合わせてある。 */
export interface NodeSnapshot {
  id: string;
  parentId: string | null;
  position: string;
  text: string;
  note: string | null;
  collapsed: boolean;
  completed: boolean;
  /** 削除印。null なら生きている。 */
  deletedAt: string | null;
}

/**
 * 適用先の抽象。Durable Object は SQLite に、ブラウザはメモリの Map に適用する。
 * **同じ `applyOp` を両方が使う**ための最小の口。
 */
export interface NodeStore {
  get(id: string): NodeSnapshot | null;
  put(node: NodeSnapshot): void;
}

export function emptyNode(id: string): NodeSnapshot {
  return {
    id,
    parentId: null,
    position: "",
    text: "",
    note: null,
    collapsed: false,
    completed: false,
    deletedAt: null,
  };
}

/**
 * 操作を 1 つ適用する。**副作用はストアへの `put` だけ**で、順序の判断も通信もしない。
 *
 * 存在しないノードへの更新は黙って捨てる（相手側で削除済みのものに追いついた場合に起きる）。
 * 例外を投げると同期が止まってしまい、1 件の取りこぼしがワークスペース全体を巻き添えにする。
 */
export function applyOp(store: NodeStore, op: SyncOp, at: string): void {
  switch (op.kind) {
    case "insert": {
      const existing = store.get(op.nodeId);
      if (existing) return; // 冪等: 同じ挿入が二度届いても増やさない
      store.put({
        ...emptyNode(op.nodeId),
        parentId: op.parentId,
        position: op.position,
        text: op.text ?? "",
      });
      return;
    }
    case "update": {
      const n = store.get(op.nodeId);
      if (!n) return;
      store.put({ ...n, text: op.text });
      return;
    }
    case "updateNote": {
      const n = store.get(op.nodeId);
      if (!n) return;
      const note = op.note != null && op.note.trim().length > 0 ? op.note : null;
      store.put({ ...n, note });
      return;
    }
    case "setCollapsed": {
      const n = store.get(op.nodeId);
      if (!n) return;
      store.put({ ...n, collapsed: op.value });
      return;
    }
    case "setCompleted": {
      const n = store.get(op.nodeId);
      if (!n) return;
      store.put({ ...n, completed: op.value });
      return;
    }
    case "move": {
      const n = store.get(op.nodeId);
      if (!n) return;
      // 自分自身を親にすると木から切り離されて画面からも検索からも消える。
      // Mac 側が `OpError.selfParent` で最後の砦を張っているのと同じ理由でここでも塞ぐ。
      if (op.parentId === op.nodeId) return;
      store.put({ ...n, parentId: op.parentId, position: op.position });
      return;
    }
    case "delete": {
      const n = store.get(op.nodeId);
      if (!n || n.deletedAt) return;
      store.put({ ...n, deletedAt: at });
      return;
    }
    case "rowUpsert":
    case "rowDelete":
      // 行の操作はノードの射影より広い（表をまたぐ）ので `applyRowOp` が受け持つ。
      // ここで黙って捨てるのは、ブラウザのメモリ上の木に Mac 由来の他表の行を
      // 混ぜないため。ノートの行として要るぶんは `applyAny` が射影して渡す。
      return;
  }
}

/** メモリ上のストア。ブラウザの楽観適用と、テストで使う。 */
export class MemoryNodeStore implements NodeStore {
  readonly nodes = new Map<string, NodeSnapshot>();
  get(id: string): NodeSnapshot | null {
    return this.nodes.get(id) ?? null;
  }
  put(node: NodeSnapshot): void {
    this.nodes.set(node.id, node);
  }
  /** 生きているノードだけを、親ごとに位置順で返す（描画用）。 */
  children(parentId: string | null): NodeSnapshot[] {
    return [...this.nodes.values()]
      .filter((n) => n.deletedAt == null && n.parentId === parentId)
      .sort((a, b) => (a.position < b.position ? -1 : a.position > b.position ? 1 : 0));
  }
}
